import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table } from '@org/ui';
import { trpc } from '@org/clients/school';
import { GenericModal } from '@org/ui';
import { SearchActions } from '@org/ui';
import { Button } from '@org/ui';
import { UnitMultiStepForm } from './unit-multi-step-form';
import { unwrapTrpcData } from '@org/ui';

type UnitsListProps = {
  onCreateUnit?: () => void;
  onEditUnit?: (unit: any) => void;
};

export const UnitsList: React.FC<UnitsListProps> = ({ onCreateUnit, onEditUnit }) => {
  const { data } = trpc.units.list.useQuery();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const [unitData, setUnitData] = React.useState<ReturnType<typeof unwrapTrpcData<typeof data>>>(null);
  const [selectedUnit, setSelectedUnit] = React.useState(null);
  const [isCreating, setIsCreating] = React.useState(false);

  // Update state when `data` changes
  React.useEffect(() => {
    setUnitData(unwrapTrpcData(data) ?? null);
  }, [data]);

  const selectUnit = (row: any) => {
    if (onEditUnit) {
      onEditUnit(row.original);
      return;
    }

    setIsCreating(false);
    setModalOpen(true)
    setSelectedUnit(row.original);
  }

  // Function to open form for a new unit
  const openNewUnitForm = () => {
    if (onCreateUnit) {
      onCreateUnit();
      return;
    }

    setSelectedUnit(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const onUnitFormSubmit = () => {
    setModalOpen(false);
  }

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row: any) => row.name ? `${row.name}` : `Unit name not assigned`, {
      id: 'name',
      header: 'Name',
    }),
    columnHelper.accessor((row: any) => row.description || '', {
      id: 'description',
      header: 'Description',
    }),
    columnHelper.display({
      id: 'edit',
      header: '',
      cell: (info) => (
        <div>
          <Button variant="outline" size="sm" onClick={() => selectUnit(info.row)}>
            Edit
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <div>
      <div><SearchActions 
          onClickAdd={openNewUnitForm} 
          onFilterChange={handleFilterChange} 
        />
        </div>
      <div><Table 
        tableData={unitData as any} 
        columns={columns} 
        getRowId={(row: any) => row.unitId} 
        globalFilter={filterValue} 
      /></div>
      {!onCreateUnit && !onEditUnit && modalOpen ? (
        <div><GenericModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isCreating ? "Create Unit" : "Edit Unit"}
          body={
            <div>
              <UnitMultiStepForm 
                defaultValues={selectedUnit || {}}
                onSubmit={onUnitFormSubmit}
                onClose={() => setModalOpen(false)}
                isEditing={!isCreating}
              />
            </div>
          }
          footer={
            <>
            </>
          }
          size="xl"
          centered
        />
        </div>
      ) : null}
    </div>
  )
}
